import type {
  CreateUnsettlementsActionRequest,
  UnsettlementFormUpdateData
} from '@library/types/unsettlements';
import {
  UnselttlementsHeader,
  UnsettlementsCreateForm
} from '@bo-components/unsettlements';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfirmationInput } from '@library/components/inputs';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { PageBody } from '@library/components/layout/PageBody';
import { PageFooterSteps } from '@library/components/layout/PageFooterSteps';
import { extractErrorMessage } from '@bo-utils/api';
import { formatProjectionScoreData } from '@bo-utils/unsettlements';
import { useCreateUnsettlementsActionMutation } from '@bo-services/unsettlementsApi';
import { useNavigate } from 'react-router-dom';
import { useProjectionById } from '@bo-hooks/projections/useProjectionById';

export const UnsettlementsCreate = () => {
  // HOOKS
  const navigate = useNavigate();

  // Initialize newUnsettlementData with a default structure
  const [newUnsettlementData, setNewUnsettlementData] =
    useState<CreateUnsettlementsActionRequest>({
      is_win_protected: undefined,
      projection_id: undefined,
      unsettlement_reason: undefined,
      updated_score_attributes: {
        score: undefined,
        unders_win_dnp: undefined,
        is_off_the_board: undefined
      }
    });

  const [createUnsettlementsAction, { isError, isLoading, isSuccess, error }] =
    useCreateUnsettlementsActionMutation();
  const {
    data: projectionData,
    isError: isProjectionError,
    isLoading: isProjectionDataLoading
  } = useProjectionById({
    projectionId: newUnsettlementData?.projection_id
  });

  const [confirmationInputValue, setConfirmationInputValue] = useState('');
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<'resettlement' | 'unsettlement'>(
    'resettlement'
  );

  useEffect(() => {
    if (isSuccess) {
      setNewUnsettlementData({
        is_win_protected: undefined,
        projection_id: undefined,
        unsettlement_reason: undefined,
        updated_score_attributes: {
          score: undefined,
          unders_win_dnp: undefined,
          is_off_the_board: undefined
        }
      });
      navigate('/unsettlements');
    }
    if (isError) {
      setIsConfirmationModalOpen(false);
    }
  }, [isError, isSuccess, navigate]);

  // HANDLERS/CONSTANTS

  const originalProjectionDetails = useMemo(
    () => formatProjectionScoreData(projectionData),
    [projectionData]
  );
  const handleOnCreate = useCallback(async () => {
    setConfirmationInputValue('');
    if (newUnsettlementData) {
      await createUnsettlementsAction(newUnsettlementData);
    }
  }, [createUnsettlementsAction, newUnsettlementData]);

  const handleOnSetUnsettlementData = useCallback(
    (data: UnsettlementFormUpdateData) => {
      const {
        isDnp,
        isReboot,
        isWinProtected,
        projectionIdValue,
        reasonValue,
        score
      } = data;

      const numericProjectionId = projectionIdValue
        ?.toString()
        .replace(/[^0-9]/g, '');

      const numericScore = score?.toString().replace(/[^0-9.]/g, '');
      const reasonCode = reasonValue?.value?.toString() || undefined;
      const isResettlementTab = activeTab === 'resettlement';

      setNewUnsettlementData((prevState) => ({
        ...prevState,
        is_win_protected: isResettlementTab ? isWinProtected : false,
        projection_id: numericProjectionId,
        unsettlement_reason: reasonCode,
        updated_score_attributes: {
          score: isResettlementTab
            ? numericScore
            : originalProjectionDetails?.score,
          unders_win_dnp: isResettlementTab
            ? isReboot
            : originalProjectionDetails?.isReboot,
          is_off_the_board: isResettlementTab
            ? isDnp
            : originalProjectionDetails?.isDnp
        }
      }));
    },
    [activeTab, originalProjectionDetails]
  );

  const handleOnCloseModal = useCallback(() => {
    setIsConfirmationModalOpen(false);
    setConfirmationInputValue('');
  }, []);

  const isProjectionValid =
    (newUnsettlementData?.projection_id ?? '').toString().length >= 4 &&
    !isProjectionError &&
    !!originalProjectionDetails;

  const isCreateDisabled =
    isLoading ||
    !newUnsettlementData?.projection_id ||
    !newUnsettlementData?.unsettlement_reason ||
    !isProjectionValid;

  const isConfirmDisabled = isLoading || confirmationInputValue !== 'CONFIRM';

  const errorMessage =
    error && typeof error === 'object' && 'status' in error
      ? extractErrorMessage(error)
      : '';

  return (
    <>
      <UnselttlementsHeader />
      <PageBody className="flex h-[calc(100%-153px)] content-center items-center justify-center">
        <UnsettlementsCreateForm
          activeTab={activeTab}
          errorMessage={errorMessage}
          handleOnSetUnsettlementData={handleOnSetUnsettlementData}
          isError={!isLoading && isError}
          isProjectionDataLoading={isProjectionDataLoading}
          isProjectionValid={isProjectionValid}
          newUnsettlementData={newUnsettlementData}
          originalProjectionDetails={originalProjectionDetails}
          setActiveTab={setActiveTab}
        />
      </PageBody>
      <ConfirmationModal
        confirmationCopy="Confirm"
        handleConfirm={handleOnCreate}
        isConfirmDisabled={isConfirmDisabled}
        isLoading={isLoading}
        isOpen={isConfirmationModalOpen}
        label="Confirm Unsettlement"
        onCancelModal={handleOnCloseModal}
      >
        <p>Are you sure you want to create this unsettlement? </p>
        <ConfirmationInput
          confirmationInputValue={confirmationInputValue}
          setConfirmationInputValue={setConfirmationInputValue}
        />
      </ConfirmationModal>
      <PageFooterSteps
        isDisabled={isCreateDisabled}
        onClickPrimary={() => setIsConfirmationModalOpen(true)}
        primaryButtonText="Continue"
      />
    </>
  );
};
